import React from 'react';
import { motion } from 'motion/react';
import { Feather, ArrowUpLeft, ArrowUpRight, Quote, Heart, Send, Check, ShieldAlert } from 'lucide-react';
import { cn } from '../lib/utils';
import { useTranslate } from '../context/LanguageContext';
import { Article } from '../types';

interface FieldOfRevolutionSectionProps {
  articles: Article[];
  isLoading: boolean;
}

/**
 * Normalizes a category string to make comparisons robust.
 */
function normalizeCategory(str: string): string {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .toLowerCase()
    .split(/[\s\-_]+/)
    .map(word => word.replace(/^ال/, ''))
    .join('')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي');
}

function matchCategory(article: Article, arabicName: string, englishNames: string[]): boolean {
  const artCatNorm = normalizeCategory(article.category || '');
  const artCatEnNorm = normalizeCategory(article.category_en || '');

  const targetArNorm = normalizeCategory(arabicName);
  const targetEnNorms = englishNames.map(en => normalizeCategory(en));

  if (artCatNorm === targetArNorm) return true;
  if (targetEnNorms.includes(artCatNorm)) return true;
  if (targetEnNorms.includes(artCatEnNorm)) return true;

  if (artCatNorm && targetArNorm && (artCatNorm.includes(targetArNorm) || targetArNorm.includes(artCatNorm))) return true;

  return false;
}

export default function FieldOfRevolutionSection({ articles, isLoading }: FieldOfRevolutionSectionProps) {
  const { language, t } = useTranslate();
  const [copiedId, setCopiedId] = React.useState<string | number | null>(null);
  const [likedIds, setLikedIds] = React.useState<Record<string | number, boolean>>({});

  const handleCopy = (e: React.MouseEvent, text: string, id: string | number) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleLike = (e: React.MouseEvent, id: string | number) => {
    e.stopPropagation();
    setLikedIds(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Filter articles belonging to "Revolution Field" (ميدان الثورة)
  const filteredArticles = (articles || [])
    .filter(a => matchCategory(a, 'ميدان الثورة', ['Revolution Field', 'RevolutionField', 'Field of Revolution', 'Revolution Square']))
    .slice(0, 4);

  // Compute read time based on content length
  const getReadTime = (content: string) => {
    if (!content) return '٣ دقائق';
    const words = content.trim().split(/\s+/).length;
    const minutes = Math.max(1, Math.ceil(words / 180));
    // Convert to Arabic numerals if lang is ar
    if (language === 'ar') {
      const arDigits = ['١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩', '١٠'];
      const textDigit = minutes <= 10 ? arDigits[minutes - 1] : String(minutes);
      return `${textDigit} دقائق`;
    }
    return `${minutes} min read`;
  };

  return (
    <section className="relative py-24 overflow-hidden bg-white dark:bg-zinc-950 border-y border-zinc-100 dark:border-zinc-900 transition-colors duration-500" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Ambient highlight */}
      <div className="absolute top-0 right-0 w-[550px] h-[550px] bg-secondary/3 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-secondary/2 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-8">
        
        {/* Minimal Section Header */}
        <div className={cn("flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 relative", language === 'ar' ? "text-right" : "text-left")}>
          <div className="space-y-4">
            <div className="flex items-center gap-2.5">
              <span className="p-1.5 bg-secondary/10 dark:bg-secondary/15 rounded-none text-secondary inline-flex items-center justify-center">
                <Feather size={15} className="transform rotate-12" />
              </span>
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.25em] text-secondary">{t('الميدان الثورة')}</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-zinc-900 dark:text-zinc-50 tracking-tight leading-none">
              {t('ميدان الثورة')}
            </h2>
            <p className="text-sm md:text-base text-zinc-500 dark:text-zinc-400 font-normal max-w-xl leading-relaxed">
              {t('أحدث الكتابات والتحليلات الثورية التي توثق الحراك المدني وتسعى لبناء الوعي وتأسيس دولة المواطنة والحرية.')}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="h-[1px] w-12 bg-zinc-200 dark:bg-zinc-800" />
            <span className="text-xs font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest font-mono">
              {t('تحليلات ثورية منتقاة')}
            </span>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 lg:gap-12 relative z-10">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="animate-pulse bg-zinc-50 dark:bg-zinc-900 p-8 rounded-none border border-zinc-200 dark:border-zinc-850 h-80 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-800 rounded w-1/4" />
                  <div className="h-6 bg-zinc-200 dark:bg-zinc-800 rounded w-3/4" />
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-800 rounded w-5/6" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-zinc-200 dark:bg-zinc-800 rounded-none shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-zinc-200 dark:bg-zinc-800 rounded w-1/2" />
                    <div className="h-2 bg-zinc-200 dark:bg-zinc-800 rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredArticles.length === 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center text-center p-12 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-none py-20 bg-zinc-50/30 dark:bg-zinc-900/5"
          >
            <ShieldAlert size={48} className="text-secondary mb-4 opacity-80" />
            <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-300 mb-2">{t('لا توجد مقالات بعد')}</h3>
            <p className="text-sm text-zinc-500 max-w-sm leading-relaxed mb-6">
              {t('سيتم عرض المقالات الخاصة بميدان الثورة هنا بمجرد كتابتها ونشرها من قبل الإدارة.')}
            </p>
            <a href="/nour" className="px-6 py-2.5 bg-primary hover:bg-secondary text-white text-xs font-black tracking-widest uppercase transition-colors">
              {t('إضافة مقال جديد')}
            </a>
          </motion.div>
        )}

        {/* Dynamic Cards Grid */}
        {!isLoading && filteredArticles.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 lg:gap-12 relative z-10">
            {filteredArticles.map((article, idx) => {
              const isLiked = likedIds[article.id];
              const readTime = getReadTime(article.content || '');
              
              return (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileTap={{ scale: 0.97 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => {
                    window.location.href = `/article/${article.slug || article.id}`;
                  }}
                  className={cn("group cursor-pointer flex flex-col h-full bg-secondary hover:bg-zinc-950 dark:hover:bg-black p-8 rounded-none border border-zinc-200/60 dark:border-zinc-800/60 hover:border-zinc-950 dark:hover:border-zinc-900 transition-all duration-500 shadow-sm hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.4)] hover:-translate-y-2 relative overflow-hidden", language === 'ar' ? "text-right" : "text-left")}
                >
                  {/* Top gild line on hover */}
                  <div className="absolute top-0 right-0 left-0 h-[4px] bg-gradient-to-l from-transparent via-secondary/0 to-transparent group-hover:via-secondary/80 transition-all duration-700" />
                  
                  {/* Card Header */}
                  <div className="flex items-center justify-between mb-8">
                    <span className="px-3 py-1 bg-black/10 group-hover:bg-white/10 w-fit text-zinc-950 group-hover:text-secondary border border-transparent group-hover:border-secondary/15 rounded-none text-[9.5px] font-black transition-all duration-500">
                      {t('ساحة الثورة')}
                    </span>
                    <span className="text-[10px] font-bold text-zinc-800 uppercase tracking-widest font-mono group-hover:text-zinc-400 transition-colors duration-300">
                      {readTime}
                    </span>
                  </div>

                  {/* Main Excerpt */}
                  <div className="relative flex-1 mb-10 space-y-4">
                    <Quote size={24} className={cn("absolute -top-4 text-black/5 pointer-events-none group-hover:text-white/10 group-hover:scale-110 transition-all duration-500", language === 'ar' ? "-right-2 rotate-180" : "-left-2")} />
                    
                    <h3 className="text-base sm:text-lg font-black text-zinc-950 group-hover:text-secondary transition-colors duration-300 tracking-tight leading-snug line-clamp-2">
                      {article.title}
                    </h3>
                    
                    <div className={cn("transition-colors duration-500 py-1.5", language === 'ar' ? "border-r border-black/10 pr-5 pl-2 group-hover:border-white/10 text-right" : "border-l border-black/10 pl-5 pr-2 group-hover:border-white/10 text-left")}>
                      <p className="text-[13.5px] text-zinc-800 font-normal leading-[1.7] line-clamp-5 relative z-10 group-hover:text-zinc-400 transition-colors duration-300">
                        {article.summary}
                      </p>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="pt-6 border-t border-black/10 group-hover:border-white/10 flex flex-col gap-5 transition-colors duration-500">
                    
                    {/* Author details */}
                    <div className="flex items-center gap-3 group/author cursor-pointer" onClick={() => window.location.href = `/author/${article.author}`}>
                      <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 shadow-lg relative ring-2 ring-black/10 group-hover/author:ring-white/10 transition-all duration-500 bg-black/10">
                        <img 
                          src={article.author_avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=150&h=150&auto=format&fit=crop'} 
                          alt={article.author || ''} 
                          className="w-full h-full object-cover transition-transform duration-700 group-hover/author:scale-110"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-extrabold text-xs text-zinc-950 truncate group-hover:text-white transition-colors duration-300">
                          {article.author || t('نورالدين كوكى')}
                        </h4>
                        <p className="text-[10px] text-zinc-800 truncate font-semibold group-hover:text-zinc-400 transition-colors duration-300">
                          {article.author_role || t('كاتب وباحث سياسي')}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={(e) => handleLike(e, article.id)}
                          className={cn(
                            "p-2 rounded-none transition-colors duration-300",
                            isLiked 
                              ? "bg-black/10 text-zinc-950" 
                              : "text-zinc-800 hover:text-zinc-950 group-hover:text-zinc-500 group-hover:hover:text-secondary hover:bg-black/5 group-hover:hover:bg-white/5"
                          )}
                          title={t("أعجبني")}
                        >
                          <Heart size={14} fill={isLiked ? "currentColor" : "none"} />
                        </button>
                        
                        <button
                          onClick={(e) => handleCopy(e, `"${article.summary}" - ${article.author || t('نورالدين كوكى')}`, article.id)}
                          className={cn(
                            "p-2 rounded-none transition-colors duration-300",
                            copiedId === article.id 
                              ? "bg-emerald-500/10 text-emerald-500" 
                              : "text-zinc-800 hover:text-zinc-950 group-hover:text-zinc-500 group-hover:hover:text-white hover:bg-black/5 group-hover:hover:bg-white/5"
                          )}
                          title={t("نسخ الاقتباس")}
                        >
                          {copiedId === article.id ? <Check size={14} /> : <Send size={14} className="-rotate-12" />}
                        </button>
                      </div>

                      <div className="flex items-center gap-1.5 text-zinc-950 group-hover:text-secondary font-black transition-colors text-[11px]">
                        <span className={cn("text-[9.5px]/none font-extrabold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-all duration-300", language === 'ar' ? "-translate-x-2 group-hover:translate-x-0" : "translate-x-2 group-hover:translate-x-0")}>
                          {t("عرض المقال")}
                        </span>
                        {language === 'ar' ? (
                          <ArrowUpLeft size={16} className="transform group-hover:scale-110 group-hover:-translate-y-0.5 group-hover:-translate-x-0.5 transition-transform duration-300" />
                        ) : (
                          <ArrowUpRight size={16} className="transform group-hover:scale-110 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300" />
                        )}
                      </div>
                    </div>

                  </div>

                </motion.div>
              );
            })}
          </div>
        )}

      </div>
    </section>
  );
}
